import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Copy, Check, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmbedCard } from "@/components/EmbedCard";
import qrBadge from "@/assets/menuone-qr-badge.png";
import { TablePdfCard } from "@/components/TablePdfCard";
import { Button } from "@/components/ui/button";
import { menuLink, PUBLIC_URL } from "@/lib/publicUrl";

const QR_SIZE = 240;
const COPIED_RESET_MS = 1800;

export default function QRCodePage() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef();

  useEffect(() => { (async () => setRestaurant((await api.get("/restaurant")).data))(); }, []);

  const menuUrl = restaurant ? menuLink(restaurant.slug) : "";
  const shareUrl = restaurant ? `${PUBLIC_URL}/api/l/${restaurant.slug}` : "";
  const [copiedShare, setCopiedShare] = useState(false);
  const copyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopiedShare(true);
    setTimeout(() => setCopiedShare(false), COPIED_RESET_MS);
    toast.success("Link per social copiato");
  };
  const useOwnLogo = !!user?.is_paid && !!restaurant?.logo_path;
  const qrLogo = useOwnLogo ? fileUrl(restaurant.logo_path) : qrBadge;


  const download = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `menuone-${restaurant.slug}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR Code scaricato");
  };

  const copy = () => {
    navigator.clipboard.writeText(menuUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_RESET_MS);
    toast.success("Link copiato");
  };

  if (!restaurant) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  return (
    <div className="space-y-8" data-testid="qr-page">
      <div>
        <h1 className="font-display font-bold text-4xl">Il tuo QR Code</h1>
        <p className="text-gray-500 mt-1">Scarica, stampa e posiziona il QR sui tavoli o sui tuoi social.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl mo-card p-8 flex flex-col items-center">
          <div ref={wrapRef} className="p-6 rounded-2xl bg-white border border-gray-100" data-testid="qr-canvas">
            <QRCodeCanvas value={menuUrl} size={QR_SIZE} level="H" includeMargin fgColor="#0b0c0b"
              imageSettings={{ src: qrLogo, height: 52, width: 52, excavate: true, crossOrigin: "anonymous" }} />
          </div>
          <p className="text-xs text-gray-400 mt-2" data-testid="qr-logo-note">{useOwnLogo ? "Al centro: il logo del tuo locale" : user?.is_paid ? "Carica il logo del locale nelle Impostazioni per vederlo al centro del QR" : "Al centro: logo MenuOne (piano Free)"}</p>
          <p className="mt-4 font-display font-semibold text-xl">{restaurant.name}</p>
          <Button onClick={download} className="mt-5 rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid="download-qr-btn"><Download className="h-4 w-4 mr-2" />Scarica PNG</Button>
        </div>

        <div className="rounded-2xl mo-card p-8">
          <h3 className="font-display font-semibold text-2xl mb-4">Link pubblico</h3>
          <div className="flex items-center gap-2 mb-6">
            <div className="flex-1 truncate rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-600 bg-gray-50" data-testid="menu-url">{menuUrl}</div>
            <Button variant="outline" size="icon" onClick={copy} className="rounded-lg shrink-0 border-gray-300 bg-white hover:bg-gray-50" data-testid="copy-link-btn">{copied ? <Check className="h-4 w-4 text-[#5aa469]" /> : <Copy className="h-4 w-4" />}</Button>
          </div>
          <div className="rounded-xl border border-gray-200 bg-[#FAFBFA] p-4 mb-6" data-testid="share-link-card">
            <div className="flex items-center gap-2 mb-1"><Share2 className="h-4 w-4 text-[#5aa469]" /><h4 className="font-semibold text-sm">Link per WhatsApp e social (con anteprima)</h4></div>
            <p className="text-xs text-gray-500 mb-3">Condiviso su WhatsApp, Instagram o Facebook mostra l'anteprima "{restaurant.name} · MenuOne".</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 truncate rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-600 bg-white" data-testid="share-url">{shareUrl}</div>
              <Button variant="outline" size="icon" onClick={copyShare} className="rounded-lg shrink-0 border-gray-300 bg-white hover:bg-gray-50" data-testid="copy-share-btn">{copiedShare ? <Check className="h-4 w-4 text-[#5aa469]" /> : <Copy className="h-4 w-4" />}</Button>
            </div>
          </div>
          <ol className="space-y-3 text-sm text-gray-600">
            {["Scarica il QR Code in alta qualità", "Stampalo e mettilo sui tavoli o sulla vetrina", "Condividi il link per social sui tuoi profili", "Aggiorna il menù quando vuoi: il QR resta lo stesso"].map((t, i) => (
              <li key={t} className="flex gap-3"><span className="h-6 w-6 rounded-full bg-[#5aa469]/12 text-[#5aa469] flex items-center justify-center text-xs font-semibold shrink-0">{i + 1}</span>{t}</li>
            ))}
          </ol>
        </div>
      </div>

      {user?.subscription?.features?.embed && <EmbedCard slug={restaurant.slug} name={restaurant.name} ops={!!user?.subscription?.features?.embed_ops} />}
      {user?.subscription?.features?.table_pdf && <TablePdfCard restaurant={restaurant} menuUrl={menuUrl} logo={qrLogo} />}
    </div>
  );
}
